import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { catchError, finalize, Observable, of, pairwise, startWith, Subscription, tap } from 'rxjs';

import { Reply } from '@shared/reply/models';
import { ReplyApiService } from '../../../../services';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { AlertsService } from '@tablesready/frontend-alerts-bar';

@Component({
  selector: 'tablesready-add-edit-reply',
  templateUrl: './add-edit-reply.component.html',
})
export class AddEditReplyComponent implements OnInit, OnDestroy {
  @Input() reply$: Observable<Reply | null>;
  @Input() reply: Reply | null = null;
  title = 'Add reply';
  form: FormGroup;
  icon = AppIcon;
  submitting = false;
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly service: ReplyApiService,
    private readonly formBuilder: FormBuilder,
    private readonly modalController: FrontendModalService,
    private readonly alertsService: AlertsService,
  ) {
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      text: ['', [Validators.required, Validators.maxLength(320)]],
    });

    const dataSubscription = this.reply$
      .pipe(
        startWith(null),
        pairwise(),
        tap(([prev, curr]) => {
          this.reply = curr;
          // Reply passed or updated externally
          if (curr) {
            this.title = 'Edit reply';
            this.form.patchValue(curr);
          } else if (curr === null && prev !== null) { // Reply has been deleted externally
            this.alertsService.addInfo('Reply has been deleted.');
            this.close();
          }
        }),
      )
      .subscribe();
    this.subscriptions.add(dataSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  close(): void {
    this.modalController.dismiss();
  }

  submit(): void {
    this.submitting = true;
    const data = this.form.value;
    (this.reply ? this.service.updateById(this.reply.id, data) : this.service.create(data))
      .pipe(
        tap(() => {
          this.alertsService.addSuccess(
            'Reply successfully saved.',
            this.reply ? 'Updated:' : 'Created:',
          );
          this.close();
        }),
        catchError(error => {
          const errorMessage = error.error?.message || `Error ${this.reply ? 'editing' : 'adding'} reply.`;
          this.alertsService.addError(errorMessage);

          return of(null);
        }),
        finalize(() => this.submitting = false),
      )
      .subscribe();
  }
}
